import {
    AccountConsent,
    AccountUserConsent,
    CircleConsentConfig,
    CircleConsentConfigScopeEnum,
    CircleConsentTypes,
} from '@calm/evexia-web-sdk';

export type AccountConsentTypes =
    | 'tos'
    | 'privacy'
    | 'chdpp'
    | 'uk_international'
    | 'in_international'
    | 'cn_international'
    | 'eu_international'
    | 'au_international'
    | 'international'
    | 'parental';
export const AccountConsentTypes = {
    Tos: 'tos' as AccountConsentTypes,
    Privacy: 'privacy' as AccountConsentTypes,
    Chdpp: 'chdpp' as AccountConsentTypes,
    UkInternational: 'uk_international' as AccountConsentTypes,
    InInternational: 'in_international' as AccountConsentTypes,
    CnInternational: 'cn_international' as AccountConsentTypes,
    EuInternational: 'eu_international' as AccountConsentTypes,
    AuInternational: 'au_international' as AccountConsentTypes,
    International: 'international' as AccountConsentTypes,
    Parental: 'parental' as AccountConsentTypes,
};

export type ConsentTypes = CircleConsentTypes | AccountConsentTypes;
export const ConsentTypes = {
    ...CircleConsentTypes,
    ...AccountConsentTypes,
};

export const accountConsentTypeFieldMap = {
    [ConsentTypes.Chdpp]: 'chdppVersion',
    [ConsentTypes.Tos]: 'tosVersion',
    [ConsentTypes.Privacy]: 'privacyVersion',
    [ConsentTypes.HealthcarePortability]: 'hipaaVersion',
    [ConsentTypes.UkInternational]: 'ukInternationalVersion',
    [ConsentTypes.CnInternational]: 'cnInternationalVersion',
    [ConsentTypes.InInternational]: 'inInternationalVersion',
    [ConsentTypes.EuInternational]: 'euInternationalVersion',
    [ConsentTypes.AuInternational]: 'auInternationalVersion',
    [ConsentTypes.International]: 'internationalVersion',
    [ConsentTypes.Parental]: 'parentalVersion',
};
/**
 * The order consents should be agreed to if agreement is needed
 * TODO at some point we should be able to simply use account and circle consent types to determine this, for now we will do it explicitly
 */
export const ConsentFlow: CircleConsentTypes[] = [
    CircleConsentTypes.Gina,
    CircleConsentTypes.Chdpp,
    CircleConsentTypes.UkInternational,
    CircleConsentTypes.InInternational,
    CircleConsentTypes.CnInternational,
    CircleConsentTypes.EuInternational,
    CircleConsentTypes.AuInternational,
    CircleConsentTypes.International,
    CircleConsentTypes.Parental,
];

export type OverlappingConsentTypes = CircleConsentTypes & AccountConsentTypes;
// Cannot do this dynamically
export const OverlappingConsentTypes: Record<string, OverlappingConsentTypes> =
    {
        Chdpp: 'chdpp',
        UkInternational: 'uk_international',
        Parental: 'parental',
        InInternational: 'in_international',
        CnInternational: 'cn_international',
        EuInternational: 'eu_international',
        AuInternational: 'au_international',
        International: 'international',
    };

export type CircleConsentConfigs = Record<
    CircleConsentTypes,
    CircleConsentConfig | undefined
>;

type AccountConsentConfig = { version: number; link: string };
export type AccountConsentConfigs = Record<
    AccountConsentTypes,
    AccountConsentConfig | undefined
>;

export type ConsentVersions = Record<ConsentTypes, number>;
export type AccountConsentVersions = Pick<ConsentVersions, AccountConsentTypes>;
export type CircleConsentVersions = Pick<ConsentVersions, CircleConsentTypes>;

export type ConsentLinks = Record<ConsentTypes, string>;
export type AccountConsentLinks = Pick<ConsentLinks, AccountConsentTypes>;
export type CircleConsentLinks = Pick<ConsentLinks, CircleConsentTypes>;

export type ConsentAcceptance = Record<ConsentTypes, boolean>;
export type ConsentScopes = Record<ConsentTypes, CircleConsentConfigScopeEnum>;
export type ConsentRequirement = Record<ConsentTypes, ConsentTypeContext>;

export interface ConsentTypeContext {
    consentType: ConsentTypes;
    /** The version of the consent that the user has accepted */
    acceptedVersion: number;
    /** The version of the consent that is required */
    requiredVersion: number;
    /** if the consent is required (requiredVersion < acceptedVersion) */
    isRequired: boolean;
    /** if the consent has a required version */
    hasRequirement: boolean;
    /** if the user has consented but needs to consent again */
    isReconsent: boolean;
    /** Link to the consent document */
    link: string;
    /** Scope of the consent (circle or account) */
    scope: CircleConsentConfigScopeEnum;
}

export interface ConsentContextProps {
    accountConsents: AccountConsent[];
    accountUserConsents: AccountUserConsent[];
    sponsorConsents?: CircleConsentConfig[];
    currentSponsorConsents?: CircleConsentConfig[];
    /**
     * In the past, has been based on loss of sponsorship flow (`['healthcare_portability']` if loss)
     **/
    shouldUseCurrentSponsor?: CircleConsentTypes[];
    /** The order of the consents */
    consentFlow?: CircleConsentTypes[];
}

export interface ConsentContextState {
    consentRequirements: ConsentRequirement;
    /** The next required consent in the flow */
    nextConsentType?: ConsentTypes;
    hasAcceptedConsent: ConsentAcceptance;
    setHasAcceptedConsent: (type: ConsentTypes, value: boolean) => void;
}
